import { ref } from 'vue'

// État partagé entre la topbar et le widget Messenger flottant.
const open = ref(false)

// Assistant IA : ouverture du sous-panneau et mode agrandi (panneau lateral).
// Partage avec le layout principal pour que le contenu de la page se
// redimensionne A COTE du panneau agrandi, au lieu d'etre recouvert par lui.
const aiOpen = ref(false)

const AI_EXPANDED_KEY = 'agei:ai-assistant-expanded'
const aiExpanded = ref(localStorage.getItem(AI_EXPANDED_KEY) === '1')

const AI_SIDEBAR_WIDTH_KEY = 'agei:ai-assistant-sidebar-width'
const SIDEBAR_MIN_WIDTH = 360
// Plafond relatif a l'ecran : jamais plus de 50 % de la largeur disponible,
// pour toujours laisser au moins autant de place au contenu principal.
const SIDEBAR_MAX_RATIO = 0.5
// Largeur minimale a laisser au contenu principal pour qu'il reste lisible
// (tableaux, formulaires...) sans se deformer a cote du panneau.
const MIN_CONTENT_WIDTH = 760

function sidebarMaxWidth(): number {
  return Math.round(window.innerWidth * SIDEBAR_MAX_RATIO)
}

const aiSidebarWidth = ref<number>((() => {
  const saved = Number(localStorage.getItem(AI_SIDEBAR_WIDTH_KEY))
  if (saved && saved >= SIDEBAR_MIN_WIDTH) return Math.min(saved, sidebarMaxWidth())
  return sidebarMaxWidth()
})())

// Cote-a-cote possible seulement si l'ecran est assez large pour loger a la
// fois un panneau lisible (SIDEBAR_MIN_WIDTH) ET un contenu principal non
// deforme (MIN_CONTENT_WIDTH). En dessous, le panneau passe directement en
// plein ecran — jamais de layout ecrase ou illisible.
const SIDE_BY_SIDE_MIN_SCREEN = SIDEBAR_MIN_WIDTH + MIN_CONTENT_WIDTH
const canSideBySide = ref(window.innerWidth >= SIDE_BY_SIDE_MIN_SCREEN)
// Conserve pour compatibilite : alias de canSideBySide (meme usage cote appelants).
const isDesktopViewport = canSideBySide
window.addEventListener('resize', () => { canSideBySide.value = window.innerWidth >= SIDE_BY_SIDE_MIN_SCREEN })

// Pendant un glisser-deposer de redimensionnement, la transition CSS du
// decalage de contenu est desactivee (sinon le contenu "traine" derriere la
// souris au lieu de suivre immediatement).
const resizingSidebar = ref(false)

function setAiExpanded(v: boolean) {
  aiExpanded.value = v
  try { localStorage.setItem(AI_EXPANDED_KEY, v ? '1' : '0') } catch { /* stockage indisponible : preference non memorisee, sans consequence */ }
}

// Bornee entre la largeur minimale lisible et le plafond (50 % de l'ecran),
// et toujours assez etroite pour laisser au moins MIN_CONTENT_WIDTH au
// contenu principal (le plus contraignant des deux l'emporte).
function clampSidebarWidth(px: number): number {
  const maxForContent = window.innerWidth - MIN_CONTENT_WIDTH
  const max = Math.max(SIDEBAR_MIN_WIDTH, Math.min(sidebarMaxWidth(), maxForContent))
  return Math.min(Math.max(px, SIDEBAR_MIN_WIDTH), max)
}

function setAiSidebarWidth(px: number) {
  aiSidebarWidth.value = clampSidebarWidth(px)
}

function persistAiSidebarWidth() {
  try { localStorage.setItem(AI_SIDEBAR_WIDTH_KEY, String(aiSidebarWidth.value)) } catch { /* stockage indisponible : preference non memorisee, sans consequence */ }
}

export const useMessenger = () => ({
  open,
  show: () => { open.value = true },
  hide: () => { open.value = false },
  aiOpen,
  aiExpanded,
  aiSidebarWidth,
  isDesktopViewport,
  canSideBySide,
  resizingSidebar,
  SIDEBAR_MIN_WIDTH,
  MIN_CONTENT_WIDTH,
  setAiExpanded,
  setAiSidebarWidth,
  clampSidebarWidth,
  persistAiSidebarWidth,
})
